import React from 'react';
import { Box } from '@mui/material';

interface TokenDisplayProps {
  promptTokens: number;
  outputTokens: number;
  variant?: 'arrows' | 'slash' | 'compact';
  showLabels?: boolean;
  size?: 'small' | 'medium' | 'large';
}

/**
 * Formats a number with abbreviated notation (K, M, B)
 * @param num - The number to format
 * @returns Formatted string with abbreviation
 */
const formatNumber = (num: number): string => {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, '') + 'B';
  }
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toLocaleString();
};

/**
 * TokenDisplay - A reusable component for displaying token counts
 * Supports multiple styles and automatic number abbreviation (K, M, B)
 */
const TokenDisplay: React.FC<TokenDisplayProps> = ({
  promptTokens,
  outputTokens,
  variant = 'arrows',
  showLabels = false,
  size = 'small'
}) => {
  const formattedPrompt = formatNumber(promptTokens);
  const formattedOutput = formatNumber(outputTokens);

  const fontSize = size === 'small' ? '0.85em' : size === 'medium' ? '1em' : '1.1em';

  const baseStyle = {
    fontSize,
    fontFamily: 'monospace',
    color: 'text.secondary',
  };

  if (variant === 'arrows') {
    return (
      <Box component="span" sx={baseStyle}>
        {showLabels && <span style={{ marginRight: 4 }}>Tokens: </span>}
        ↑{formattedPrompt} | ↓{formattedOutput}
      </Box>
    );
  }

  if (variant === 'slash') {
    return (
      <Box component="span" sx={baseStyle}>
        {showLabels && <span style={{ marginRight: 4 }}>Tokens: </span>}
        {formattedPrompt} / {formattedOutput}
      </Box>
    );
  }

  if (variant === 'compact') {
    return (
      <Box component="span" sx={baseStyle}>
        {showLabels && <span style={{ marginRight: 4 }}>I/O: </span>}
        {formattedPrompt}↑ {formattedOutput}↓
      </Box>
    );
  }

  return null;
};

export default TokenDisplay;